'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logTopModuleSwiped from '@alltrails/amplitude/events/logTopModuleSwiped';
import logTrailDetailsAddPhotoClicked from '@alltrails/amplitude/events/logTrailDetailsAddPhotoClicked';
import logTrailDetailsHeroImageClicked from '@alltrails/amplitude/events/logTrailDetailsHeroImageClicked';
import logTrailDetailsTourButtonClicked from '@alltrails/amplitude/events/logTrailDetailsTourButtonClicked';
import useUser from '@alltrails/context/hooks/useUser';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import getPhotoUrl from '@alltrails/core/utils/getPhotoUrl';
import SwiperCarousel from '@alltrails/denali/components/SwiperCarousel';
import Toast from '@alltrails/denali/components/Toast';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { handleClosePhotoTour, handleOpenPhotoTour } from '@alltrails/photo-tour/redux/photoTourSlice';
import useTrailPhotos from '@alltrails/trails/hooks/useTrailPhotos';
import type TrailPhotoLocationInfo from '@alltrails/trails/types/TrailPhotoLocationInfo';
import type { TrailPage } from '@alltrails/trails/types/trailPage';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type { TrailId } from '@alltrails/types/TrailId';
import type TrailPhoto from '@alltrails/types/TrailPhoto';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import useTrailDefaultImage from '@/hooks/useTrailDefaultImage';
import getTrailPageType from '@/utils/getTrailPageType';
import AddPhotoButton from '../DesktopHeroGrid/AddPhotoButton';
import PhotoTourButton from '../DesktopHeroGrid/PhotoTourButton';
import HeroImage from '../HeroImage/HeroImage';
import MobileStaticMap from '../MobileStaticMap';
import styles from './MobileHero.module.scss';
import MobileHeroImage from './MobileHeroImage';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const TrailMap = dynamic(() => import('../TrailMap'), { ssr: false });
const PhotoTourModal = dynamic(() => import('@alltrails/photo-tour/components/PhotoTourModal'), { ssr: false });
const PhotoUploadModal = dynamic(() => import('@/components/PhotoUploadModal/PhotoUploadModalWrapper'), { ssr: false });

const photoCarouselCount = 6;

type MobileHeroProps = {
  defaultPhoto?: TrailPhoto;
  mapId: number;
  photoCount?: number;
  photoPreviewId?: string;
  trailInfo: TrailPhotoLocationInfo & { id: TrailId };
  length?: number;
  hasPhotoTour: boolean;
  isBot?: boolean;
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const MobileHero = ({
  defaultPhoto,
  mapId,
  photoCount,
  photoPreviewId,
  trailInfo,
  length,
  hasPhotoTour,
  isBot = false,
  trailDetails,
  trailPage
}: MobileHeroProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const dispatch = useDispatch();
  const user = useUser();
  const swiped = useRef(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isPhotoTourOpen, setIsPhotoTourOpen] = useState(false);
  const [wasPhotoTourClicked, setWasPhotoTourClicked] = useState(false);
  const [wasAddPhotosClicked, setWasAddPhotosClicked] = useState(false);
  const { isTrailMapOpen, openTrailMap } = useIsTrailMapOpen();
  const [isPhotoUploadModalOpen, setIsPhotoUploadModalOpen] = useState(false);
  const [signUpModalTrigger, setSignUpModalTrigger] = useState<CarouselDisplayTrigger>('View Trail Photos');
  const [upsellTrigger, setUpsellTrigger] = useState<UpsellTrigger>('View Trail Photos');
  const [isPhotoToastOpen, setIsPhotoToastOpen] = useState(false);
  const { photos, loadPhotos, loadState } = useTrailPhotos({ defaultPhoto, perPage: photoCarouselCount, trailId: trailInfo.id });
  const { defaultPhotoAlt, defaultPhotoUrl } = useTrailDefaultImage({
    defaultPhoto,
    mapId,
    photoPreviewId,
    photoSize: '1080x700',
    trailPhotoLocationInfo: trailInfo,
    trailDetails
  });

  const handleOpenTour = useCallback(() => {
    dispatch(
      handleOpenPhotoTour({
        tourType: 'trail',
        tourArgs: { trailId: trailInfo.id, isMobile }
      })
    );
    setIsPhotoTourOpen(true);
  }, [dispatch, isMobile, trailInfo.id]);

  const trailPageType = getTrailPageType(trailDetails);

  const onPhotoClick = useCallback(() => {
    logTrailDetailsHeroImageClicked({ trail_id: trailInfo.id, trail_page_type: trailPageType });
    if (!user) {
      setSignUpModalTrigger('View Trail Photos');
      setUpsellTrigger('View Trail Photos');
      setIsSignupModalOpen(true);
    } else {
      window.location.assign(`/trail/${trailInfo.slug}/photos`);
    }
  }, [trailInfo.id, trailInfo.slug, user, trailPageType]);

  const onMapFallbackClick = useCallback(() => {
    logTrailDetailsHeroImageClicked({ trail_id: trailInfo.id, trail_page_type: trailPageType });
    if (!user) {
      setSignUpModalTrigger('View Static Map');
      setUpsellTrigger('View Static Map');
      setIsSignupModalOpen(true);
    } else {
      openTrailMap();
    }
  }, [openTrailMap, trailInfo.id, user, trailPageType]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos, trailInfo.id]);

  return (
    <>
      <div className={styles.container}>
        {!!photoCount && !!defaultPhoto ? (
          <SwiperCarousel
            arrowStyle="subtle-with-standard-arrows"
            className={styles.carouselContainer}
            getItemKey={(photo: any) => String(photo.id)}
            items={photos}
            hasPaginationGradient
            hideSlideOverflow
            onSlideChange={() => {
              if (!swiped.current) {
                logTopModuleSwiped({ id: trailInfo.id.toString(), page_type: 'trail page' });
                swiped.current = true;
              }
            }}
            renderItem={(photo, photoIndex) => {
              const alt = photoIndex === 0 ? defaultPhotoAlt : trailInfo.name;
              const url =
                photoIndex === 0
                  ? defaultPhotoUrl
                  : getPhotoUrl({
                      isOptimized: true,
                      photo,
                      size: '1080x700'
                    });
              return (
                <MobileHeroImage
                  alt={alt}
                  onPhotoClick={onPhotoClick}
                  photoCount={photoCount}
                  photoIndex={photoIndex}
                  photoUrl={url}
                  showPhotosCta={loadState === 'loaded' && photos.length > 1 && photoIndex === photos.length - 1}
                  trailSlug={trailInfo.slug}
                />
              );
            }}
            scrollMode={photos.length < 2}
            showPaginationDots={true}
            uniqueId="trail-photos-mobile-carousel"
          />
        ) : (
          <button aria-label={intl.formatMessage({ defaultMessage: 'Expand map' })} className={styles.staticMapHero} onClick={onMapFallbackClick}>
            <HeroImage alt={defaultPhotoAlt} isLcp photoUrl={defaultPhotoUrl} quality={75} />
          </button>
        )}
        {hasPhotoTour && (
          <PhotoTourButton
            isMobile
            onClick={() => {
              logTrailDetailsTourButtonClicked({ trail_id: trailInfo.id, web_location: 'gallery', trail_page_type: 'trail detail page' });
              if (!user) {
                setSignUpModalTrigger('Gallery Tour');
                setUpsellTrigger('Gallery Tour');
                setWasPhotoTourClicked(true);
                setIsSignupModalOpen(true);
              } else {
                handleOpenTour();
              }
            }}
          />
        )}
        {!photoCount && (
          <AddPhotoButton
            handleClick={() => {
              logTrailDetailsAddPhotoClicked({
                trail_id: trailInfo.id
              });
              if (!user) {
                setSignUpModalTrigger('Add Photo to Trail');
                setUpsellTrigger('Add Photo to Trail');
                setWasAddPhotosClicked(true);
                setIsSignupModalOpen(true);
              } else {
                setIsPhotoUploadModalOpen(true);
              }
            }}
            isMobile
          />
        )}
        {!!defaultPhoto && !!photoCount && !isBot && (
          <div className={styles.staticMap}>
            <MobileStaticMap mapId={mapId} trailName={trailInfo.name} trailId={trailDetails.id} trailPageType={trailPageType} />
          </div>
        )}
        {isTrailMapOpen && <TrailMap isMobile={isMobile} trailName={trailInfo.name} trailDetails={trailDetails} trailPage={trailPage} />}
        {isSignupModalOpen && (
          <SignUpModal
            isOpen={isSignupModalOpen}
            closeModal={() => {
              setIsSignupModalOpen(false);
            }}
            deeplinkData={{ pageId: trailInfo.id, pageType: 'trail' }}
            onSuccess={() => {
              setIsSignupModalOpen(false);
              if (wasPhotoTourClicked) {
                handleOpenTour();
              } else if (wasAddPhotosClicked) {
                setIsPhotoUploadModalOpen(true);
              } else if (!!photoCount && !!defaultPhoto) {
                window.location.assign(`/trail/${trailInfo.slug}/photos`);
              } else {
                openTrailMap();
              }
            }}
            trigger={signUpModalTrigger}
            upsellTrigger={upsellTrigger}
          />
        )}
        {(defaultPhoto || photos?.[0]) && isPhotoTourOpen && (
          <PhotoTourModal
            trailId={trailInfo.id}
            totalDistance={length}
            segmentActualDistances={length ? [length] : undefined}
            onClose={() => {
              setIsPhotoTourOpen(false);
              dispatch(handleClosePhotoTour());
            }}
            defaultPhoto={defaultPhoto || photos?.[0]}
            secondPhoto={photos?.[1]}
            name={trailInfo.name}
            hidePageScrollbarGutter
            trailPageType="trail detail page"
          />
        )}
      </div>
      {isPhotoUploadModalOpen && (
        <ModalPortal>
          <PhotoUploadModal
            closeModal={() => setIsPhotoUploadModalOpen(false)}
            trailId={trailInfo.id}
            onPhotosAdd={() => setIsPhotoToastOpen(true)}
            shouldHideSuccess
          />
        </ModalPortal>
      )}
      {isPhotoToastOpen && (
        <Toast
          isOpen={isPhotoToastOpen}
          message={<FormattedMessage defaultMessage="Success! Your photo will be available soon." />}
          type="neutral"
          timeout={3000}
          setIsOpen={setIsPhotoToastOpen}
          position="alternative"
        />
      )}
    </>
  );
};

export default MobileHero;
